/**
 * Upgrade FAQ Component
 * Accordion-style FAQ section
 */

import { useState } from 'react';
import { HelpCircle, ChevronDown } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { FAQ_ITEMS } from '../utils/constants';

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ question, answer, isOpen, onToggle }: FAQItemProps) {
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 text-left group"
      >
        <span className="font-medium text-gray-900 group-hover:text-primary-600 transition-colors">
          {question}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-40 pb-4' : 'max-h-0'
        }`}
      >
        <p className="text-sm text-gray-600 leading-relaxed">{answer}</p>
      </div>
    </div>
  );
}

export function UpgradeFAQ() {
  const { t } = useLanguage();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const handleToggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Translated FAQ items
  const translatedFAQItems = [
    {
      question: t('upgrade.faq.cancelQuestion', FAQ_ITEMS[0].question),
      answer: t('upgrade.faq.cancelAnswer', FAQ_ITEMS[0].answer),
    },
    {
      question: t('upgrade.faq.dataQuestion', FAQ_ITEMS[1].question),
      answer: t('upgrade.faq.dataAnswer', FAQ_ITEMS[1].answer),
    },
    {
      question: t('upgrade.faq.switchQuestion', FAQ_ITEMS[2].question),
      answer: t('upgrade.faq.switchAnswer', FAQ_ITEMS[2].answer),
    },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200 animate-fade-in animation-delay-300">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <HelpCircle className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {t('upgrade.faq.title', 'Frequently Asked Questions')}
            </h2>
            <p className="text-sm text-gray-500">
              {t('upgrade.faq.subtitle', 'Everything you need to know')}
            </p>
          </div>
        </div>
      </div>

      {/* FAQ Items */}
      <div className="px-6">
        {translatedFAQItems.map((item, index) => (
          <FAQItem
            key={index}
            question={item.question}
            answer={item.answer}
            isOpen={openIndex === index}
            onToggle={() => handleToggle(index)}
          />
        ))}
      </div>
    </div>
  );
}
